import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Target, Shield, TrendingUp, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6
  }
};
export function AboutPage() {
  return <div className="w-full pt-36 lg:pt-44 bg-navy" data-id="element-58">
      {/* Hero */}
      <section className="bg-navy py-16 md:py-20" data-id="element-59">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-60">
          <motion.div className="max-w-4xl mx-auto text-center" initial="initial" animate="animate" variants={fadeInUp} data-id="element-61">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6" data-id="element-62">
              About Strategic Metals Group
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto mb-6 md:mb-8" data-id="element-63" />
            <p className="text-lg md:text-xl text-steel leading-relaxed max-w-2xl mx-auto" data-id="element-64">
              A global advisory firm bridging investment, exploration,
              production, and distribution across the precious metals supply
              chain.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="bg-white py-16 md:py-20" data-id="element-65">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-66">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-67">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-68">
              Who We Are
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto mb-8 md:mb-12" data-id="element-69" />
            <div className="space-y-5 md:space-y-6 text-base md:text-lg text-charcoal leading-relaxed" data-id="element-70">
              <p data-id="element-71">
                Strategic Metals Group Inc. is a Canada-based global advisory
                firm specializing in connecting investors with high-potential
                natural resources opportunities around the world. We leverage an
                extensive international network to facilitate strategic
                partnerships between early-stage explorers, developers, mine
                operators, and qualified investors seeking exposure to gold,
                silver, and other precious metals and commodities.
              </p>
              <p data-id="element-72">
                In addition to capital matchmaking, we help mining projects
                secure reliable revenue streams by identifying and engaging
                buyers across global markets, especially in the Middle East,
                through structured off-take agreements.
              </p>
              <p data-id="element-73">
                Strategic Metals Group Inc. bridges the gap between investment,
                exploration, production, and distribution, delivering end-to-end
                solutions across the precious metals supply chain.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-74">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-75">
          <motion.div className="text-center mb-12 md:mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-76">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-77">
              What We Do
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto" data-id="element-78" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-79">
            {[{
            icon: Users,
            title: 'Connect Stakeholders',
            description: 'We bring together investors, mining companies, operators, and commodity buyers to create mutually beneficial partnerships.'
          }, {
            icon: TrendingUp,
            title: 'Facilitate Capital Access',
            description: 'We match early-stage explorers and developers with qualified investors seeking exposure to high-potential natural resources.'
          }, {
            icon: Globe,
            title: 'Structure Off-Take Agreements',
            description: 'We help mining projects secure long-term revenue channels through relationships with international buyers.'
          }, {
            icon: Shield,
            title: 'Provide Strategic Advisory',
            description: 'We offer end-to-end guidance across investment, exploration, production, and distribution.'
          }, {
            icon: Target,
            title: 'Enable Market Access',
            description: 'We leverage our international network to unlock cross-border opportunities in key markets.'
          }, {
            icon: Award,
            title: 'Deliver Value',
            description: 'We focus on creating long-term value for all stakeholders through structured, strategic partnerships.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-80">
                <Card className="h-full border border-gold/20 hover:border-gold transition-all" data-id="element-81">
                  <CardContent className="p-5 md:p-6" data-id="element-82">
                    <item.icon className="h-10 w-10 md:h-12 md:w-12 text-gold mb-4" data-id="element-83" />
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-2 md:mb-3" data-id="element-84">
                      {item.title}
                    </h3>
                    <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-85">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Where We Operate */}
      <section className="bg-white py-16 md:py-20" data-id="element-86">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-87">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-88">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-89">
              Where We Operate
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto mb-8 md:mb-12" data-id="element-90" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8" data-id="element-91">
              {[{
              region: 'Canada',
              description: 'Our Canada-based operations provide deep expertise in North American mining sectors and capital markets.'
            }, {
              region: 'Middle East',
              description: 'We maintain strategic relationships with buyers and investors across Middle Eastern markets, particularly for off-take agreements.'
            }, {
              region: 'Global Markets',
              description: 'Our extensive international network spans commodity trading and investment hubs worldwide.'
            }].map((item, index) => <motion.div key={index} className="text-center" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15
            }} data-id="element-92">
                  <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gold rounded-full mb-4" data-id="element-93">
                    <Globe className="h-7 w-7 md:h-8 md:w-8 text-navy" data-id="element-94" />
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-2 md:mb-3" data-id="element-95">
                    {item.region}
                  </h3>
                  <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-96">
                    {item.description}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why SMG */}
      <section className="bg-navy py-16 md:py-20" data-id="element-97">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-98">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-99">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6 text-center" data-id="element-100">
              Why Strategic Metals Group
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto mb-8 md:mb-12" data-id="element-101" />
            <div className="space-y-4 md:space-y-6" data-id="element-102">
              {[{
              title: 'Extensive Global Network',
              description: 'We maintain trusted relationships with investors, mining companies, operators, and commodity buyers across international markets.'
            }, {
              title: 'Deep Sector Expertise',
              description: 'Our team brings decades of combined experience in precious metals, mining, and capital markets.'
            }, {
              title: 'Structured Approach',
              description: 'We design financing and revenue frameworks that support long-term value creation for all stakeholders.'
            }, {
              title: 'Discretion and Trust',
              description: 'We handle all engagements with the highest level of confidentiality and professionalism.'
            }, {
              title: 'End-to-End Support',
              description: 'From initial opportunity identification to execution, we provide comprehensive advisory support.'
            }].map((item, index) => <motion.div key={index} className="bg-navy-light border-l-4 border-gold p-5 md:p-6 rounded" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} data-id="element-103">
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-gold mb-2" data-id="element-104">
                    {item.title}
                  </h3>
                  <p className="text-steel leading-relaxed text-sm md:text-base" data-id="element-105">
                    {item.description}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-106">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-107">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-108">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-109">
              Our Approach
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold-bar mx-auto mb-8 md:mb-12" data-id="element-110" />
            <div className="space-y-5 md:space-y-6 text-base md:text-lg text-charcoal leading-relaxed" data-id="element-111">
              <p data-id="element-112">
                At Strategic Metals Group Inc., we believe that successful
                partnerships are built on trust, transparency, and strategic
                alignment. Our approach is methodical and relationship-driven:
              </p>
              <ul className="space-y-3 md:space-y-4 ml-4 md:ml-6" data-id="element-113">
                <li className="flex items-start" data-id="element-114">
                  <span className="text-gold mr-3 mt-1 flex-shrink-0" data-id="element-115">•</span>
                  <span data-id="element-116">
                    <strong className="text-navy" data-id="element-117">Deep Understanding:</strong>{' '}
                    We take the time to understand each stakeholder's
                    objectives, constraints, and strategic priorities.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-118">
                  <span className="text-gold mr-3 mt-1 flex-shrink-0" data-id="element-119">•</span>
                  <span data-id="element-120">
                    <strong className="text-navy" data-id="element-121">Strategic Matching:</strong>{' '}
                    We leverage our global network to identify and connect the
                    right partners based on strategic fit.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-122">
                  <span className="text-gold mr-3 mt-1 flex-shrink-0" data-id="element-123">•</span>
                  <span data-id="element-124">
                    <strong className="text-navy" data-id="element-125">
                      Structured Frameworks:
                    </strong>{' '}
                    We design capital and off-take structures that create
                    sustainable value for all parties.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-126">
                  <span className="text-gold mr-3 mt-1 flex-shrink-0" data-id="element-127">•</span>
                  <span data-id="element-128">
                    <strong className="text-navy" data-id="element-129">Ongoing Support:</strong> We
                    provide continuous advisory support throughout the
                    partnership lifecycle.
                  </span>
                </li>
              </ul>
              <p data-id="element-130">
                Our commitment is to facilitate partnerships that deliver
                long-term value, market access, and strategic advantage across
                the precious metals supply chain.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}